import { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, Trash2, Save, Undo2, GripVertical, X, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { useBranch } from '../../context/BranchContext';
import { useV3Shell } from '../../v3/V3ShellContext';
import { getPageZoom } from '../../utils/desktopZoomFix';
import { elementDef, listElementDefs, elementMinSizes } from '../../spaces/elements/registry';
import { GRID_COLS, ROW_UNIT, ROW_GAP, cellFromPoint, layoutRows, moveElement, resizeElement, placeNewElement } from '../../spaces/layoutGrid';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ORANGE = '#ee7203';
const clone = (value) => JSON.parse(JSON.stringify(value));
const newId = () => `el-${Math.random().toString(36).slice(2, 10)}`;
const fieldClass = 'h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-300';
const MIN_SIZES = elementMinSizes();

const BUILDER_CSS = `
.sb-canvas { position: relative; display: grid; grid-template-columns: repeat(${GRID_COLS}, minmax(0, 1fr)); gap: ${ROW_GAP}px; padding: 0; border-radius: 16px; background: rgba(148, 163, 184, .08); border: 1px dashed rgba(148, 163, 184, .55); user-select: none; touch-action: none; }
.sb-col { grid-row: 1 / -1; border-radius: 8px; background: rgba(148, 163, 184, .10); pointer-events: none; }
.sb-el { position: relative; z-index: 1; min-width: 0; min-height: 0; display: flex; flex-direction: column; border-radius: 14px; border: 2px solid rgba(238, 114, 3, .35); background: #ffffff; box-shadow: 0 6px 18px -10px rgba(0, 0, 0, .35); cursor: grab; overflow: hidden; }
.sb-el.is-sel { border-color: #ee7203; box-shadow: 0 0 0 3px rgba(238, 114, 3, .22), 0 6px 18px -10px rgba(0, 0, 0, .35); }
.sb-el.is-drag { cursor: grabbing; opacity: .92; }
.sb-el-head { flex: none; display: flex; align-items: center; gap: 6px; padding: 4px 6px 4px 8px; font-size: 11px; font-weight: 700; color: #1d1d1b; background: rgba(238, 114, 3, .10); }
.sb-el-size { margin-left: auto; font-weight: 600; color: #6b7280; }
.sb-el-body { flex: 1 1 auto; min-height: 0; overflow: hidden; padding: 10px; pointer-events: none; }
.sb-el-body--dark { background: #181220; } .sb-el-body--light { background: #f3eff7; }
.sb-handle { position: absolute; right: 0; bottom: 0; width: 22px; height: 22px; cursor: nwse-resize; background: linear-gradient(135deg, transparent 50%, #ee7203 50%); border-bottom-right-radius: 10px; touch-action: none; }
.sb-card { border: 1px solid #e5e7eb; border-radius: 14px; background: #ffffff; padding: 10px; }
.sb-card:hover { border-color: rgba(238, 114, 3, .6); }
.sb-card-preview { height: 118px; overflow: hidden; border-radius: 10px; padding: 8px; pointer-events: none; }
.sb-pill { display: inline-flex; align-items: center; gap: 6px; height: 34px; padding: 0 12px; border-radius: 999px; border: 1px solid #d1d5db; background: #ffffff; font-size: 13px; font-weight: 600; color: #374151; cursor: pointer; }
.sb-pill.is-on { border-color: #ee7203; background: rgba(238, 114, 3, .10); color: #1d1d1b; }
`;

/**
 * SETTINGS > USER INTERFACE > SPACES BUILDER
 * Make a page layout for a Space's home page: give it a name and a Space, then fill the page with elements. The page on screen is the home page itself (the same 12-column grid, the
 * same places and sizes): drag an element to move it, pull its corner to resize it, or drag one in from the element list. Every kind of element is in the list with a preview, and
 * can be set up (its size, and whatever has been designed to be changed on that element) before it is added or after it is on the page.
 * Everything is saved by the Save button; nothing on the live home page changes until then. Only a super admin gets here (Settings.jsx).
 */
export default function SpacesBuilderPanel() {
  const { getEffectiveBranchId } = useBranch();
  const branchId = getEffectiveBranchId();
  const v3Shell = useV3Shell();
  const dark = v3Shell ? v3Shell.theme === 'dark' : false;
  const canvasRef = useRef(null);
  const dragStop = useRef(null);

  const [layouts, setLayouts] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [draft, setDraft] = useState(null);       // { id, name, sector_id, elements } being worked on
  const [saved, setSaved] = useState('');         // the draft as last saved, to know if there is anything unsaved
  const [selectedEl, setSelectedEl] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [tab, setTab] = useState('elements');
  const [templates, setTemplates] = useState({}); // what a new element of each kind starts with: { [type]: { size, props } }
  const [openCustomise, setOpenCustomise] = useState(null);
  const [newName, setNewName] = useState('');
  const [newSector, setNewSector] = useState('');
  const [busy, setBusy] = useState(false);

  const dirty = !!draft && JSON.stringify(draft) !== saved;
  const defs = listElementDefs();
  const templateOf = (def) => {
    const own = templates[def.type] || {};
    return { size: own.size || def.sizes[0], props: own.props || def.defaultProps() };
  };

  const loadLists = useCallback(async () => {
    if (!branchId) return;
    try {
      const [lay, sec] = await Promise.all([
        api.get(`${API}/nav-header/space-layouts`, { params: { branch_id: branchId } }),
        api.get(`${API}/nav-header/sectors`, { params: { branch_id: branchId } }),
      ]);
      setLayouts(lay.data?.layouts || []);
      setSectors(sec.data?.sectors || []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not load the Spaces Builder');
    }
  }, [branchId]);

  useEffect(() => { setDraft(null); setSaved(''); setSelectedEl(null); loadLists(); }, [loadLists]);

  const openLayout = (layout) => {
    const next = { id: layout.id, name: layout.name, sector_id: layout.sector_id || '', elements: clone(layout.elements || []) };
    setDraft(next); setSaved(JSON.stringify(next)); setSelectedEl(null); setTab('elements');
  };

  const chooseLayout = async (id) => {
    if (draft && draft.id === id) return;
    if (dirty && !window.confirm('This page has changes that are not saved. Leave without saving them?')) return;
    try {
      const res = await api.get(`${API}/nav-header/space-layouts/${id}`);
      openLayout(res.data);
    } catch (error) { toast.error(error?.response?.data?.detail || 'Could not open that layout'); }
  };

  const createLayout = async () => {
    if (!newName.trim()) { toast.error('Give the new page a name'); return; }
    if (dirty && !window.confirm('This page has changes that are not saved. Leave without saving them?')) return;
    setBusy(true);
    try {
      const res = await api.post(`${API}/nav-header/space-layouts`, { branch_id: branchId, name: newName.trim(), sector_id: newSector || null });
      setNewName(''); setNewSector('');
      await loadLists();
      openLayout(res.data);
      toast.success('Page created');
    } catch (error) { toast.error(error?.response?.data?.detail || 'Could not create the page'); }
    finally { setBusy(false); }
  };

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      const res = await api.put(`${API}/nav-header/space-layouts/${draft.id}`, { name: draft.name, sector_id: draft.sector_id || null, elements: draft.elements });
      openLayout(res.data);
      await loadLists();
      toast.success('Saved');
    } catch (error) { toast.error(error?.response?.data?.detail || 'Could not save'); }
    finally { setBusy(false); }
  };

  const revert = () => { if (saved) { setDraft(JSON.parse(saved)); setSelectedEl(null); } };

  const remove = async () => {
    if (!draft || !window.confirm(`Delete the page "${draft.name}"? ${draft.sector_id ? 'Its Space goes back to the Default home page.' : ''}`)) return;
    setBusy(true);
    try {
      await api.delete(`${API}/nav-header/space-layouts/${draft.id}`);
      setDraft(null); setSaved(''); setSelectedEl(null);
      await loadLists();
      toast.success('Page deleted');
    } catch (error) { toast.error(error?.response?.data?.detail || 'Could not delete'); }
    finally { setBusy(false); }
  };

  const setElements = (change) => setDraft((current) => (current ? { ...current, elements: change(current.elements) } : current));

  const addElement = (def, cell = null) => {
    const template = templateOf(def);
    const base = { id: newId(), type: def.type, x: 0, y: 0, w: template.size.w, h: template.size.h, props: clone(template.props) };
    let placedId = base.id;
    setElements((elements) => {
      const result = placeNewElement(elements, base, cell, MIN_SIZES);
      placedId = result.placed.id;
      return result.elements;
    });
    setSelectedEl(placedId);
    setTab('settings');
  };

  const removeElement = (id) => { setElements((elements) => elements.filter((el) => el.id !== id)); if (selectedEl === id) setSelectedEl(null); };
  const setProps = (id, props) => setElements((elements) => elements.map((el) => (el.id === id ? { ...el, props } : el)));
  const setSize = (id, w, h) => setElements((elements) => resizeElement(elements, id, w, h, MIN_SIZES));

  // Dragging an element on the page: 'move' (by its header or body) or 'resize' (by its corner). The page is measured as it is on screen, so this is right at any zoom.
  const startDrag = (event, el, mode) => {
    if (event.button !== undefined && event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const zoom = getPageZoom() || 1;
    const grab = cellFromPoint(rect, event.clientX, event.clientY, zoom);
    const offset = { x: grab.x - el.x, y: grab.y - el.y };
    setSelectedEl(el.id);
    setTab('settings');
    setDragging(el.id);
    const onMove = (moveEvent) => {
      const cell = cellFromPoint(canvas.getBoundingClientRect(), moveEvent.clientX, moveEvent.clientY, zoom);
      if (mode === 'resize') setElements((elements) => resizeElement(elements, el.id, cell.x - el.x + 1, cell.y - el.y + 1, MIN_SIZES));
      else setElements((elements) => moveElement(elements, el.id, cell.x - offset.x, cell.y - offset.y, MIN_SIZES));
    };
    const stop = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
      dragStop.current = null;
      setDragging(null);
    };
    dragStop.current = stop;
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
  };
  useEffect(() => () => { if (dragStop.current) dragStop.current(); }, []);

  // An element dragged in from the element list.
  const onDrop = (event) => {
    event.preventDefault();
    const text = event.dataTransfer ? event.dataTransfer.getData('text/plain') : '';
    const type = text.startsWith('element:') ? text.slice(8) : null;
    const def = type ? elementDef(type) : null;
    if (!def || !draft || !canvasRef.current) return;
    addElement(def, cellFromPoint(canvasRef.current.getBoundingClientRect(), event.clientX, event.clientY, getPageZoom() || 1));
  };

  const rows = draft ? Math.max(layoutRows(draft.elements) + 2, 9) : 9;
  const selected = draft ? draft.elements.find((el) => el.id === selectedEl) : null;
  const selectedDef = selected ? elementDef(selected.type) : null;
  const bodyTheme = dark ? 'sb-el-body--dark' : 'sb-el-body--light';

  return (
    <div className="space-y-5" data-testid="spaces-builder">
      <style>{BUILDER_CSS}</style>

      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-gray-200 bg-white p-4">
        <div>
          <label className="block text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1" htmlFor="sb-new-name">New page</label>
          <input id="sb-new-name" value={newName} onChange={(event) => setNewName(event.target.value)} maxLength={60} placeholder="Name, e.g. Sales front page" className={`${fieldClass} w-64`} data-testid="sb-new-name" />
        </div>
        <div>
          <label className="block text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1" htmlFor="sb-new-sector">For the Space</label>
          <select id="sb-new-sector" value={newSector} onChange={(event) => setNewSector(event.target.value)} className={`${fieldClass} w-56`} data-testid="sb-new-sector">
            <option value="">(choose later)</option>
            {sectors.map((sector) => <option key={sector.id} value={sector.id}>{sector.name}</option>)}
          </select>
        </div>
        <button type="button" onClick={createLayout} disabled={busy} className="h-9 px-4 rounded-lg text-sm font-semibold text-white flex items-center gap-1.5 hover:opacity-90 disabled:opacity-50" style={{ backgroundColor: ORANGE }} data-testid="sb-create"><Plus className="w-4 h-4" /> Create page</button>
        <div className="flex flex-wrap gap-2 ml-auto" data-testid="sb-space-list">
          {layouts.length === 0 && <span className="text-sm text-gray-500">No pages yet.</span>}
          {layouts.map((layout) => (
            <button key={layout.id} type="button" className={`sb-pill ${draft && draft.id === layout.id ? 'is-on' : ''}`} onClick={() => chooseLayout(layout.id)} title={layout.sector_name ? `Home page of ${layout.sector_name}` : 'Not given to a Space yet'} data-testid={`sb-space-${layout.id}`}>
              {layout.name}<span className="text-[11px] font-semibold text-gray-500">{layout.sector_name || 'no Space'}</span>
            </button>
          ))}
        </div>
      </div>

      {!draft ? (
        <div className="rounded-2xl border border-dashed border-gray-300 p-10 text-center text-sm text-gray-500" data-testid="sb-empty">Choose a page above, or create a new one, to arrange its home page.</div>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-gray-200 bg-white p-4" data-testid="sb-toolbar">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1" htmlFor="sb-name">Page name</label>
              <input id="sb-name" value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} maxLength={60} className={`${fieldClass} w-64`} data-testid="sb-name" />
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1" htmlFor="sb-sector">Home page of this Space</label>
              <select id="sb-sector" value={draft.sector_id} onChange={(event) => setDraft({ ...draft, sector_id: event.target.value })} className={`${fieldClass} w-56`} data-testid="sb-sector">
                <option value="">(no Space)</option>
                {sectors.map((sector) => <option key={sector.id} value={sector.id}>{sector.name}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2 ml-auto">
              {dirty && <span className="text-xs font-semibold text-orange-600" data-testid="sb-dirty">Unsaved changes</span>}
              <button type="button" onClick={revert} disabled={!dirty || busy} className="h-9 px-3 rounded-lg border border-gray-300 bg-white text-sm font-semibold text-gray-700 flex items-center gap-1.5 hover:bg-gray-50 disabled:opacity-40" data-testid="sb-revert"><Undo2 className="w-4 h-4" /> Undo changes</button>
              <button type="button" onClick={save} disabled={!dirty || busy} className="h-9 px-4 rounded-lg text-sm font-semibold text-white flex items-center gap-1.5 hover:opacity-90 disabled:opacity-40" style={{ backgroundColor: ORANGE }} data-testid="sb-save"><Save className="w-4 h-4" /> Save</button>
              <button type="button" onClick={remove} disabled={busy} className="h-9 w-9 rounded-lg border border-red-200 bg-white text-red-600 flex items-center justify-center hover:bg-red-50" aria-label="Delete this page" data-testid="sb-delete"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>

          <div className="flex flex-col xl:flex-row gap-5 items-start">
            <div className="min-w-0 flex-1 w-full">
              <div ref={canvasRef} className="sb-canvas" style={{ gridTemplateRows: `repeat(${rows}, ${ROW_UNIT}px)`, minHeight: rows * ROW_UNIT }} onDragOver={(event) => event.preventDefault()} onDrop={onDrop} onPointerDown={() => setSelectedEl(null)} data-testid="sb-canvas">
                {Array.from({ length: GRID_COLS }).map((_, index) => <div key={index} className="sb-col" style={{ gridColumn: index + 1 }} aria-hidden="true" />)}
                {draft.elements.map((el) => {
                  const def = elementDef(el.type);
                  if (!def) return null;
                  const Preview = def.Preview;
                  return (
                    <div
                      key={el.id}
                      className={`sb-el ${selectedEl === el.id ? 'is-sel' : ''} ${dragging === el.id ? 'is-drag' : ''}`}
                      style={{ gridColumn: `${el.x + 1} / span ${el.w}`, gridRow: `${el.y + 1} / span ${el.h}` }}
                      onPointerDown={(event) => startDrag(event, el, 'move')}
                      data-testid={`sb-element-${el.id}`}
                    >
                      <div className="sb-el-head">
                        <GripVertical className="w-3.5 h-3.5 text-gray-500" />
                        <span>{def.name}</span>
                        <span className="sb-el-size" data-testid={`sb-size-${el.id}`}>{el.w} × {el.h}</span>
                        <button type="button" className="ml-1 w-5 h-5 rounded flex items-center justify-center text-gray-600 hover:bg-red-100 hover:text-red-600" onPointerDown={(event) => event.stopPropagation()} onClick={() => removeElement(el.id)} aria-label={`Remove ${def.name}`} data-testid={`sb-remove-${el.id}`}><X className="w-3.5 h-3.5" /></button>
                      </div>
                      <div className={`sb-el-body ${bodyTheme}`}><Preview props={el.props || {}} dark={dark} /></div>
                      <span className="sb-handle" onPointerDown={(event) => startDrag(event, el, 'resize')} aria-label="Resize" data-testid={`sb-resize-${el.id}`} />
                    </div>
                  );
                })}
              </div>
              <p className="mt-2 text-xs text-gray-500">This is the home page. Drag an element to move it, pull its orange corner to resize it, or drag one in from the list.</p>
            </div>

            <div className="w-full xl:w-[360px] flex-none rounded-2xl border border-gray-200 bg-white p-4 space-y-3" data-testid="sb-side">
              <div className="flex gap-2">
                <button type="button" className={`sb-pill ${tab === 'elements' ? 'is-on' : ''}`} onClick={() => setTab('elements')} data-testid="sb-tab-elements">Elements</button>
                <button type="button" className={`sb-pill ${tab === 'settings' ? 'is-on' : ''}`} onClick={() => setTab('settings')} data-testid="sb-tab-settings"><SlidersHorizontal className="w-3.5 h-3.5" /> This element</button>
              </div>

              {tab === 'elements' && (
                <div className="space-y-3" data-testid="sb-elements">
                  {defs.map((def) => {
                    const template = templateOf(def);
                    const Preview = def.Preview;
                    const Editor = def.Editor;
                    return (
                      <div key={def.type} className="sb-card" draggable onDragStart={(event) => { event.dataTransfer.setData('text/plain', `element:${def.type}`); event.dataTransfer.effectAllowed = 'copy'; }} data-testid={`sb-palette-${def.type}`}>
                        <div className={`sb-card-preview ${bodyTheme}`}><Preview props={template.props} dark={dark} /></div>
                        <p className="mt-2 text-sm font-bold text-gray-900">{def.name}</p>
                        <p className="text-xs text-gray-500">{def.description}</p>
                        <div className="mt-2 flex gap-2">
                          <button type="button" onClick={() => addElement(def)} className="h-8 px-3 rounded-lg text-xs font-semibold text-white flex items-center gap-1 hover:opacity-90" style={{ backgroundColor: ORANGE }} data-testid={`sb-add-${def.type}`}><Plus className="w-3.5 h-3.5" /> Add to page</button>
                          <button type="button" onClick={() => setOpenCustomise(openCustomise === def.type ? null : def.type)} className="h-8 px-3 rounded-lg border border-gray-300 bg-white text-xs font-semibold text-gray-700 hover:bg-gray-50" data-testid={`sb-customise-${def.type}`}>{openCustomise === def.type ? 'Done' : 'Set up'}</button>
                        </div>
                        {openCustomise === def.type && (
                          <div className="mt-3 space-y-3 border-t border-gray-200 pt-3" data-testid={`sb-setup-${def.type}`}>
                            <div>
                              <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1">Size when added</p>
                              <div className="flex flex-wrap gap-1.5">
                                {def.sizes.map((size) => (
                                  <button key={size.id} type="button" className={`sb-pill ${template.size.id === size.id ? 'is-on' : ''}`} onClick={() => setTemplates({ ...templates, [def.type]: { ...templates[def.type], size } })} data-testid={`sb-template-size-${def.type}-${size.id}`}>{size.label}</button>
                                ))}
                              </div>
                            </div>
                            <Editor props={template.props} onChange={(props) => setTemplates({ ...templates, [def.type]: { ...templates[def.type], props } })} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {tab === 'settings' && (
                <div className="space-y-4" data-testid="sb-settings">
                  {!selected || !selectedDef ? (
                    <p className="text-sm text-gray-500" data-testid="sb-nothing-selected">Click an element on the page to change its size or what it shows.</p>
                  ) : (
                    <>
                      <p className="text-sm font-bold text-gray-900">{selectedDef.name}</p>
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1">Size</p>
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {selectedDef.sizes.map((size) => (
                            <button key={size.id} type="button" className={`sb-pill ${selected.w === size.w && selected.h === size.h ? 'is-on' : ''}`} onClick={() => setSize(selected.id, size.w, size.h)} data-testid={`sb-size-preset-${size.id}`}>{size.label}</button>
                          ))}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <label className="flex items-center gap-1.5">Columns <input type="number" min={selectedDef.minW} max={GRID_COLS} value={selected.w} onChange={(event) => setSize(selected.id, Number(event.target.value) || selectedDef.minW, selected.h)} className={`${fieldClass} w-16`} data-testid="sb-w" /></label>
                          <label className="flex items-center gap-1.5">Rows <input type="number" min={selectedDef.minH} max={24} value={selected.h} onChange={(event) => setSize(selected.id, selected.w, Number(event.target.value) || selectedDef.minH)} className={`${fieldClass} w-16`} data-testid="sb-h" /></label>
                        </div>
                      </div>
                      <selectedDef.Editor props={selected.props || {}} onChange={(props) => setProps(selected.id, props)} />
                      <button type="button" onClick={() => removeElement(selected.id)} className="h-8 px-3 rounded-lg border border-red-200 bg-white text-xs font-semibold text-red-600 hover:bg-red-50 flex items-center gap-1" data-testid="sb-remove-selected"><Trash2 className="w-3.5 h-3.5" /> Take off the page</button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
